import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardAppShell from '../components/DashboardAppShell';
import DataPrivacyPromise from '../components/DataPrivacyPromise';
import { useApp } from '../context/AppContext';

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const EXPENSE_CATEGORIES = [
  { id: 'medication', label: 'Medication', icon: '💊' },
  { id: 'consultation', label: 'Consultation', icon: '🩺' },
  { id: 'lab', label: 'Lab work', icon: '🔬' },
  { id: 'retrieval', label: 'Retrieval', icon: '🥚' },
  { id: 'transfer', label: 'Transfer', icon: '📋' },
  { id: 'genetic', label: 'Genetic testing (PGT)', icon: '🧬' },
  { id: 'storage', label: 'Embryo storage', icon: '❄️' },
  { id: 'other', label: 'Other', icon: '📌' },
];

const IVF_ESTIMATE_DEFAULTS = {
  baseCycle: 15000,
  medications: 5500,
  geneticTesting: 2500,
  embryoStorage: 800,
  frozenTransfer: 3500,
  anesthesia: 800,
  numCycles: 1,
  numTransfers: 1,
};

const REGION_MULTIPLIERS = {
  national: 1,
  northeast: 1.15,
  west: 1.2,
  south: 0.85,
  midwest: 0.9,
};

const NATIONAL_AVG_BY_CATEGORY = [
  { label: 'Base cycle (retrieval)', avg: 15000, color: 'var(--pink-m)' },
  { label: 'Medications (stims)', avg: 5500, color: 'var(--pink)' },
  { label: 'Genetic testing (PGT)', avg: 2500, color: 'var(--pink-l)' },
  { label: 'Frozen transfer', avg: 3500, color: '#F4C0D1' },
  { label: 'Anesthesia', avg: 800, color: '#E8D5DC' },
  { label: 'Embryo storage', avg: 800, color: '#DCE8E4' },
];

const NATIONAL_AVG_TOTAL = NATIONAL_AVG_BY_CATEGORY.reduce((s, c) => s + c.avg, 0);

const DEMO_EXPENSES = [
  { id: 'demo1', category: 'medication', description: 'Gonal-F 900 IU', amount: 2840, date: '2025-03-01', receiptUrl: null },
  { id: 'demo2', category: 'medication', description: 'Menopur 75 IU', amount: 420, date: '2025-03-02', receiptUrl: null },
  { id: 'demo3', category: 'consultation', description: 'Cycle monitoring visit', amount: 185, date: '2025-03-08', receiptUrl: null },
  { id: 'demo4', category: 'lab', description: 'Baseline labs', amount: 320, date: '2025-02-28', receiptUrl: null },
];

function formatCurrency(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

export default function FinancialTracker() {
  const navigate = useNavigate();
  const { user, getFertilityFinances, addExpense, removeExpense, saveIvfEstimate } = useApp();
  const finances = getFertilityFinances?.(user) || { expenses: [], ivfEstimate: null };
  const expenses = user ? finances.expenses : DEMO_EXPENSES;
  const ivfEstimate = finances.ivfEstimate;

  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ category: 'medication', description: '', amount: '', date: new Date().toISOString().slice(0, 10), receiptUrl: null });
  const [estimateForm, setEstimateForm] = useState(() => ({ ...IVF_ESTIMATE_DEFAULTS, ...(ivfEstimate || {}) }));
  const [estimateRegion, setEstimateRegion] = useState(ivfEstimate?.region || 'national');
  const receiptInputRef = useRef(null);

  const totalSpent = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const estimatedTotal = Math.round(
    ((estimateForm.baseCycle || 0) +
      (estimateForm.medications || 0) +
      (estimateForm.geneticTesting || 0) +
      (estimateForm.embryoStorage || 0) +
      ((estimateForm.frozenTransfer || 0) * (estimateForm.numTransfers || 1)) +
      (estimateForm.anesthesia || 0)) *
      (estimateForm.numCycles || 1) *
      (REGION_MULTIPLIERS[estimateRegion] || 1)
  );

  function handleAddExpense(e) {
    e.preventDefault();
    if (!addForm.amount || Number(addForm.amount) <= 0) return;
    addExpense({
      category: addForm.category,
      description: addForm.description,
      amount: Number(addForm.amount),
      date: addForm.date,
      receiptUrl: addForm.receiptUrl,
    });
    setAddForm({ category: 'medication', description: '', amount: '', date: new Date().toISOString().slice(0, 10), receiptUrl: null });
    setShowAddForm(false);
  }

  function handleReceiptUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => setAddForm((f) => ({ ...f, receiptUrl: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function handleSaveEstimate() {
    saveIvfEstimate({ ...estimateForm, region: estimateRegion });
  }

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 };
  const isDemo = !user;

  return (
    <DashboardAppShell fontFamily={FONT_BODY}>
        {isDemo && (
          <div style={{ background: 'linear-gradient(135deg, #FBEAF0, #EEEDFE)', border: '1px solid var(--pink-m)', borderRadius: 12, padding: 16, marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>Example view</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>Join the waitlist to track your own costs and save your estimate when we launch.</div>
            </div>
            <button type="button" onClick={() => navigate('/signup')} style={{ fontSize: 13, fontWeight: 700, color: 'white', background: 'var(--pink)', border: 'none', padding: '10px 20px', borderRadius: 999, cursor: 'pointer', flexShrink: 0 }}>
              Sign up free →
            </button>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 28 }}>
          <div>
            <h1 style={{ fontFamily: FONT_SERIF, fontSize: 28, color: 'var(--text)', marginBottom: 6 }}>Financial tracker</h1>
            <p style={{ fontSize: 14, color: 'var(--muted)' }}>Track spending, upload receipts, and estimate your total IVF costs.</p>
          </div>
          {user?.email && (
            <button type="button" onClick={() => navigate(`/profile/${encodeURIComponent(user.email)}`)} style={{ fontSize: 13, fontWeight: 700, color: 'var(--pink)', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
              View & edit your profile →
            </button>
          )}
        </div>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Total spent</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--text)' }}>{formatCurrency(totalSpent)}</div>
          </div>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Estimated total</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--pink-d)' }}>{formatCurrency(estimatedTotal)}</div>
          </div>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Remaining (est.)</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--sage-d)' }}>{formatCurrency(Math.max(0, estimatedTotal - totalSpent))}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 28, alignItems: 'start' }}>
          {/* Expense tracking */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontFamily: FONT_SERIF, fontSize: 18, color: 'var(--text)', margin: 0 }}>Track spending</h2>
              {!isDemo && (
                <button
                  type="button"
                  onClick={() => setShowAddForm(!showAddForm)}
                  style={{ fontSize: 13, fontWeight: 700, color: 'white', background: 'var(--pink)', border: 'none', padding: '8px 16px', borderRadius: 999, cursor: 'pointer' }}
                >
                  {showAddForm ? 'Cancel' : '+ Add expense'}
                </button>
              )}
              {isDemo && (
                <button type="button" onClick={() => navigate('/signup')} style={{ fontSize: 13, fontWeight: 700, color: 'var(--pink)', background: 'var(--pink-l)', border: '1px solid var(--pink-m)', padding: '8px 16px', borderRadius: 999, cursor: 'pointer' }}>
                  Join the waitlist to save expenses →
                </button>
              )}
            </div>

            {showAddForm && !isDemo && (
              <form onSubmit={handleAddExpense} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Category</label>
                    <select value={addForm.category} onChange={(e) => setAddForm((f) => ({ ...f, category: e.target.value }))} style={inputStyle}>
                      {EXPENSE_CATEGORIES.map((c) => (
                        <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Amount ($)</label>
                    <input type="number" min={0} step={0.01} value={addForm.amount} onChange={(e) => setAddForm((f) => ({ ...f, amount: e.target.value }))} style={inputStyle} placeholder="0" required />
                  </div>
                </div>
                <div style={{ marginTop: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Description</label>
                  <input type="text" value={addForm.description} onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))} style={inputStyle} placeholder="e.g. Gonal-F 900 IU" />
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Date</label>
                    <input type="date" value={addForm.date} onChange={(e) => setAddForm((f) => ({ ...f, date: e.target.value }))} style={{ ...inputStyle, width: 140 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Receipt</label>
                    <input ref={receiptInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleReceiptUpload} />
                    <button type="button" onClick={() => receiptInputRef.current?.click()} style={{ fontSize: 12, padding: '8px 14px', borderRadius: 10, border: '1px dashed var(--border)', background: 'var(--sand)', color: 'var(--muted)', cursor: 'pointer' }}>
                      {addForm.receiptUrl ? '✓ Receipt added' : 'Upload receipt'}
                    </button>
                  </div>
                  <button type="submit" style={{ marginTop: 20, fontSize: 13, fontWeight: 700, color: 'white', background: 'var(--pink)', border: 'none', padding: '10px 20px', borderRadius: 999, cursor: 'pointer' }}>
                    Save expense
                  </button>
                </div>
              </form>
            )}

            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
              {expenses.length === 0 && !isDemo ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>No expenses yet. Add medication costs, consultations, and procedure fees as you go.</div>
              ) : (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {expenses.map((exp) => {
                    const cat = EXPENSE_CATEGORIES.find((c) => c.id === exp.category);
                    return (
                      <div key={exp.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 20 }}>{cat?.icon || '📌'}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{exp.description || cat?.label || 'Expense'}</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{exp.date} · {cat?.label}</div>
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>{formatCurrency(exp.amount)}</div>
                        {exp.receiptUrl && <span style={{ fontSize: 10, color: 'var(--sage-d)' }}>📎</span>}
                        {!isDemo && <button type="button" onClick={() => removeExpense(exp.id)} style={{ fontSize: 12, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }} aria-label="Remove">×</button>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <DataPrivacyPromise />
          </div>

          {/* IVF cost estimator */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, position: 'sticky', top: 24 }}>
            <h2 style={{ fontFamily: FONT_SERIF, fontSize: 18, color: 'var(--text)', marginBottom: 16 }}>IVF cost estimator</h2>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 18 }}>Adjust ranges for your clinic. Based on 2024–2025 US averages.</p>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Region</label>
              <select value={estimateRegion} onChange={(e) => setEstimateRegion(e.target.value)} style={inputStyle}>
                <option value="national">National average</option>
                <option value="northeast">Northeast (+15%)</option>
                <option value="west">West (+20%)</option>
                <option value="south">South (-15%)</option>
                <option value="midwest">Midwest (-10%)</option>
              </select>
            </div>

            {[
              { key: 'baseCycle', label: 'Base cycle (retrieval)', min: 10000, max: 25000, step: 500 },
              { key: 'medications', label: 'Medications (stims)', min: 2000, max: 10000, step: 500 },
              { key: 'geneticTesting', label: 'Genetic testing (PGT)', min: 0, max: 6000, step: 500 },
              { key: 'embryoStorage', label: 'Embryo storage /yr', min: 0, max: 2000, step: 100 },
              { key: 'frozenTransfer', label: 'Frozen transfer (each)', min: 2000, max: 6000, step: 250 },
              { key: 'anesthesia', label: 'Anesthesia', min: 0, max: 2000, step: 100 },
              { key: 'numCycles', label: 'Number of cycles', min: 1, max: 5, step: 1 },
              { key: 'numTransfers', label: 'Transfers (frozen)', min: 0, max: 5, step: 1 },
            ].map(({ key, label, min, max, step }) => (
              <div key={key} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <label style={{ fontWeight: 600, color: 'var(--text)' }}>{label}</label>
                  <span style={{ fontWeight: 700, color: 'var(--pink-d)' }}>
                    {key.startsWith('num') ? estimateForm[key] : formatCurrency(estimateForm[key] || 0)}
                  </span>
                </div>
                <input
                  type="range"
                  min={min}
                  max={max}
                  step={step}
                  value={estimateForm[key] ?? min}
                  onChange={(e) => setEstimateForm((f) => ({ ...f, [key]: Number(e.target.value) }))}
                  style={{ width: '100%', accentColor: 'var(--pink)' }}
                />
              </div>
            ))}

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Estimated total</span>
                <span style={{ fontSize: 20, fontWeight: 900, color: 'var(--pink-d)' }}>{formatCurrency(estimatedTotal)}</span>
              </div>
              <button type="button" onClick={isDemo ? () => navigate('/signup') : handleSaveEstimate} style={{ width: '100%', fontSize: 13, fontWeight: 700, color: 'white', background: 'var(--pink)', border: 'none', padding: '12px', borderRadius: 10, cursor: 'pointer' }}>
                {isDemo ? 'Join waitlist to save estimate' : 'Save estimate'}
              </button>
            </div>
            <DataPrivacyPromise />
          </div>
        </div>

        {/* National average chart */}
        <div style={{ marginTop: 32, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 24 }}>
          <h2 style={{ fontFamily: FONT_SERIF, fontSize: 18, color: 'var(--text)', marginBottom: 6 }}>National average — IVF cost breakdown</h2>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 20 }}>Typical US costs per category (2024–2025). Compare to your estimate above.</p>
          <div style={{ maxWidth: 520 }}>
            {NATIONAL_AVG_BY_CATEGORY.map(({ label, avg, color }) => {
              const pct = (avg / NATIONAL_AVG_TOTAL) * 100;
              return (
                <div key={label} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, color: 'var(--text)' }}>{label}</span>
                    <span style={{ fontWeight: 700, color: 'var(--pink-d)' }}>{formatCurrency(avg)}</span>
                  </div>
                  <div style={{ height: 10, background: 'var(--sand)', borderRadius: 6, overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${pct}%`,
                        height: '100%',
                        background: color,
                        borderRadius: 6,
                        transition: 'width 0.4s ease',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)' }}>National average total</span>
            <span style={{ fontSize: 18, fontWeight: 900, color: 'var(--text)' }}>{formatCurrency(NATIONAL_AVG_TOTAL)}</span>
          </div>
        </div>
    </DashboardAppShell>
  );
}
